import React from 'react';
import { UserIcon, CalendarIcon, HeartIcon } from 'lucide-react';
interface PartnerData {
  username: string;
  lastCheckInDate: string;
  achievements: string[];
  status: string;
}
interface PartnerScreenProps {
  partner: PartnerData | null;
}
const ACHIEVEMENT_MAP: Record<string, {
  emoji: string;
  label: string;
}> = {
  salah: {
    emoji: '☪️',
    label: 'Prayed 5 salah'
  },
  quran: {
    emoji: '📖',
    label: 'Read Quran'
  },
  exercise: {
    emoji: '💪',
    label: 'Exercised'
  },
  work: {
    emoji: '📚',
    label: 'Worked/studied'
  },
  dhikr: {
    emoji: '📿',
    label: 'Made dhikr'
  },
  helped: {
    emoji: '🤝',
    label: 'Helped someone'
  },
  outside: {
    emoji: '🌳',
    label: 'Went outside'
  },
  'ate-healthy': {
    emoji: '🍎',
    label: 'Ate healthy'
  },
  sleep: {
    emoji: '😴',
    label: 'Slept on time'
  }
};
const STATUS_DISPLAY: Record<string, {
  emoji: string;
  label: string;
  color: string;
}> = {
  'clean-strong': {
    emoji: '💚',
    label: 'Clean and strong',
    color: 'text-green-600'
  },
  'clean-struggling': {
    emoji: '💛',
    label: 'Clean but struggling',
    color: 'text-yellow-600'
  },
  setback: {
    emoji: '🔄',
    label: 'Had a setback',
    color: 'text-muted-foreground'
  }
};
export function PartnerScreen({
  partner
}: PartnerScreenProps) {
  if (!partner) {
    return <main className="flex flex-col items-center justify-center px-5 pt-20 pb-32 min-h-screen" data-id="element-143">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-5" data-id="element-144">
          <UserIcon className="w-8 h-8 text-primary" data-id="element-145" />
        </div>
        <h1 className="text-xl font-bold text-foreground text-center mb-3" data-id="element-146">
          No partner yet
        </h1>
        <p className="text-base text-muted-foreground text-center max-w-xs leading-relaxed mb-6" data-id="element-147">
          You'll be matched with a brother soon, insha'Allah. For now, the panic
          button is always here for you.
        </p>
        <div className="bg-card rounded-lg border border-border p-4 w-full max-w-xs" data-id="element-148">
          <p className="text-sm text-muted-foreground text-center italic" data-id="element-149">
            "And cooperate in righteousness and piety."
          </p>
          <p className="text-xs text-muted-foreground text-center mt-1" data-id="element-150">
            — Surah Al-Ma'idah (5:2)
          </p>
        </div>
      </main>;
  }
  const statusInfo = STATUS_DISPLAY[partner.status] || STATUS_DISPLAY['clean-strong'];
  return <main className="flex flex-col px-5 pt-10 pb-32 min-h-screen" data-id="element-151">
      {/* Header */}
      <div className="mb-6" data-id="element-152">
        <h1 className="text-2xl font-bold text-foreground mb-1" data-id="element-153">
          Your accountability partner
        </h1>
        <p className="text-base text-muted-foreground" data-id="element-154">
          You're in this together, brother.
        </p>
      </div>

      {/* Partner card */}
      <div className="bg-card rounded-lg border border-border p-5 mb-6" data-id="element-155">
        <div className="flex items-center gap-4 mb-5" data-id="element-156">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-157">
            <span className="text-lg font-bold text-primary" data-id="element-158">
              {partner.username.charAt(0).toUpperCase()}
            </span>
          </div>
          <div data-id="element-159">
            <p className="font-semibold text-foreground text-lg" data-id="element-160">
              {partner.username}
            </p>
            <div className="flex items-center gap-1.5 mt-1" data-id="element-161">
              <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground" data-id="element-162" />
              <p className="text-sm text-muted-foreground" data-id="element-163">
                Last check-in: {partner.lastCheckInDate}
              </p>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-background rounded-lg p-3.5 mb-4 flex items-center gap-2.5" data-id="element-164">
          <span className="text-lg" role="img" aria-hidden="true" data-id="element-165">
            {statusInfo.emoji}
          </span>
          <span className={`font-medium text-sm ${statusInfo.color}`} data-id="element-166">
            {statusInfo.label}
          </span>
        </div>

        {/* Achievements */}
        {partner.achievements.length > 0 && <div data-id="element-167">
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2.5" data-id="element-168">
              Recent achievements
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-169">
              {partner.achievements.map(achievementId => {
            const achievement = ACHIEVEMENT_MAP[achievementId];
            if (!achievement) return null;
            return <span key={achievementId} className="inline-flex items-center gap-1.5 bg-highlight/30 text-foreground text-xs font-medium px-3 py-1.5 rounded-full border border-primary/20" data-id="element-170">
                    <span role="img" aria-hidden="true" data-id="element-171">
                      {achievement.emoji}
                    </span>
                    {achievement.label}
                  </span>;
          })}
            </div>
          </div>}
      </div>

      {/* Encouragement */}
      <div className="bg-card rounded-lg border border-border p-4 flex items-start gap-3" data-id="element-172">
        <HeartIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" data-id="element-173" />
        <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-174">
          Your partner can see your check-ins too. Knowing someone's watching —
          even anonymously — makes a difference. Keep showing up.
        </p>
      </div>
    </main>;
}